import React, { useState, useEffect } from 'react';
import { Bell, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { Reminder, ReminderStatus } from '../../types/reminder';
import { parseLocalDate } from '../../utils/dateUtils';

interface PetOwnerRemindersWidgetProps {
  pets: Array<{ id: string; name: string }>;
}

const PetOwnerRemindersWidget: React.FC<PetOwnerRemindersWidgetProps> = ({ pets }) => {
  const [allReminders, setAllReminders] = useState<Array<Reminder & { pet_name: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (pets.length > 0) {
      fetchAllReminders();
    }
  }, [pets]);

  const fetchAllReminders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch reminders for all pets
      const reminderPromises = pets.map(async (pet) => {
        const response = await fetch(API_ENDPOINTS.REMINDERS.BY_PET(pet.id), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          return (data.reminders || []).map((reminder: Reminder) => ({
            ...reminder,
            pet_name: pet.name
          }));
        }
        return [];
      });

      const results = await Promise.all(reminderPromises);
      const flattenedReminders = results.flat();
      
      // Sort by due date
      flattenedReminders.sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      );
      
      setAllReminders(flattenedReminders);
    } catch (err) {
      console.error('Failed to fetch reminders:', err);
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (dueDate: string) => {
    const date = parseLocalDate(dueDate);
    const now = new Date();
    return date < now && date.toDateString() !== now.toDateString();
  };

  const formatDate = (dateString: string) => {
    const date = parseLocalDate(dateString);
    const now = new Date();
    // Reset both to start of day for accurate day comparison
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffTime = dateStart.getTime() - nowStart.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `${Math.abs(diffDays)}d overdue`;
    } else if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays <= 7) {
      return `${diffDays}d`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const activeReminders = allReminders.filter(r => 
    r.status === ReminderStatus.PENDING
  );
  
  const overdueReminders = activeReminders.filter(r => isOverdue(r.due_date));
  const upcomingReminders = activeReminders.filter(r => !isOverdue(r.due_date));

  if (loading) {
    return (
      <section>
        <h3 className="text-base font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Reminders
        </h3>
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        </div>
      </section>
    );
  }

  if (activeReminders.length === 0) {
    return (
      <section>
        <h3 className="text-base font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Reminders
        </h3>
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
          <CheckCircle className="w-6 h-6 mx-auto mb-1 text-gray-300" />
          <p className="text-sm">No active reminders</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-500 uppercase tracking-wider">
          Reminders
        </h3>
        <div className="flex items-center gap-2 text-sm">
          {overdueReminders.length > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
              {overdueReminders.length} overdue
            </span>
          )}
          {upcomingReminders.length > 0 && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
              {upcomingReminders.length} upcoming
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {/* Overdue Reminders */}
        {overdueReminders.slice(0, 3).map((reminder) => (
          <div
            key={reminder.id}
            className="p-2.5 border-2 border-red-200 bg-red-50 rounded-lg"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {reminder.pet_name}
                  </span>
                  <span className="text-xs text-red-600 font-medium">
                    {formatDate(reminder.due_date)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 truncate">{reminder.title}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Upcoming Reminders */}
        {upcomingReminders.slice(0, overdueReminders.length > 0 ? 2 : 5).map((reminder) => (
          <div
            key={reminder.id}
            className="p-2.5 border border-gray-200 bg-white rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {reminder.pet_name}
                  </span>
                  <span className="text-xs text-gray-600">
                    {formatDate(reminder.due_date)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 truncate">{reminder.title}</p>
              </div>
            </div>
          </div>
        ))}

        {/* Show more indicator */}
        {activeReminders.length > 5 && (
          <div className="text-center py-2">
            <p className="text-xs text-gray-500">
              +{activeReminders.length - 5} more reminders
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PetOwnerRemindersWidget;

